import { Component, CUSTOM_ELEMENTS_SCHEMA, HostListener, OnInit } from "@angular/core";
import { LOGIN_TRANSLATIONS } from "./login.translations";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { AuthService } from "../../../core/services/auth.service";
import { cancelScheduledRefresh } from "../../../core/services/token-refresh";
import { Router, RouterLink, RouterModule } from "@angular/router";
import { LoginValidationService } from "src/app/core/services/login-validation.service";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { AlertsComponent } from "../../alerts/alerts.component";
import { PublicHeader } from "src/app/features/public/public-header/public-header";
import { SecurityUtils } from "src/app/utils/security.utils";

import { OtpService } from "src/app/services/otp.service";

type LoginMode = "department" | "ract";

@Component({
  selector: "app-login",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatSelectModule,
    MatInputModule,
    MatCheckboxModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    AlertsComponent,
    PublicHeader,
    RouterLink,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit {
  currentLang: 'en' | 'or' = 'or';

  @HostListener('window:langChange', ['$event'])
  onLangChange(event: any) {
    this.currentLang = event.detail;
  }

  get t() {
    return (LOGIN_TRANSLATIONS as any)[this.currentLang];
  }

  loginMode: LoginMode = "ract";
  loginType = "DEPARTMENT";
  isLoading = false;
  showPwd = false;
  alert = {
    alertType: "",
    alertMessage: "",
  };
  hide = true;
  authForm!: FormGroup;

  captchaUrl = '';
  captchaId = '';
  private captchaObjectUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private loginValidationService: LoginValidationService,
    private snackBar: MatSnackBar,
    private otpService: OtpService,
  ) {
    const savedLang = localStorage.getItem('app_lang') as 'en' | 'or';
    this.currentLang = savedLang === 'en' ? 'en' : 'or';
    localStorage.setItem('app_lang', this.currentLang);
  }

  ngOnInit() {
    if (this.authService.isLoggedIn()) {
      cancelScheduledRefresh();
      localStorage.clear();
      this.authService.setCurrentUser(null);
    }
    this.authForm = this.fb.group({
      username: ["", [Validators.required]],
      password: ["", [Validators.required, Validators.minLength(8)]],
      captcha: ["", [Validators.required]],
      rememberMe: [false],
    });
    this.applyLoginModeValidators();
    this.refreshCaptcha();
  }

  refreshCaptcha() {
    if (this.captchaObjectUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.captchaObjectUrl);
    }
    this.captchaObjectUrl = null;
    this.otpService.fetchCaptcha().subscribe({
      next: ({ captchaId, imageUrl }) => {
        this.captchaId = captchaId;
        this.captchaObjectUrl = imageUrl;
        this.captchaUrl = imageUrl;
      },
      error: () => {
        this.captchaId = '';
        this.captchaUrl = '';
      }
    });
  }

  setLoginMode(mode: LoginMode): void {
    if (this.loginMode === mode) return;
    this.loginMode = mode;
    this.authForm.patchValue({ username: "", password: "" });
    this.authForm.get("username")?.setErrors(null);
    this.authForm.get("password")?.setErrors(null);
    this.alert = { alertType: "", alertMessage: "" };
    this.applyLoginModeValidators();
  }

  private applyLoginModeValidators(): void {
    const usernameCtrl = this.authForm.get("username");
    usernameCtrl?.setValidators([
      Validators.required,
      Validators.pattern(
        /^([a-zA-Z0-9_-]+|[\w.-]+@[\w-]+\.[\w-]{2,4}|\d{10})$/,
      ),
    ]);
    usernameCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  onUsernameInput(event: any) {
    this.authForm.get("username")?.setErrors(null);
  }

  onPasswordInput() {
    this.authForm.get("password")?.setErrors(null);
  }

  onUsernameBlur() {
    const usernameCtrl = this.authForm.get("username");
    usernameCtrl?.markAsTouched();
    usernameCtrl?.updateValueAndValidity();
  }

  onPasswordBlur() {
    const passwordCtrl = this.authForm.get("password");
    passwordCtrl?.markAsTouched();
    passwordCtrl?.updateValueAndValidity();
  }

  onSubmit() {
    this.isLoading = true;
    try {
      if (!this.authForm.valid) {
        this.isLoading = false;
        this.authForm.markAllAsTouched();
        const errorMsg = this.t.correctFormErrors || "Please correct the errors in the form before submitting.";
        this.snackBar.open(errorMsg, "Close", {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        });
        return;
      }

      const captchaValue = this.authForm.get('captcha')?.value;
      if (!this.captchaId || !captchaValue) {
        this.isLoading = false;
        this.showError("Please enter the security CAPTCHA code.");
        return;
      }

      this.otpService.verifyCaptcha(captchaValue, this.captchaId).subscribe({
        next: (captchaRes: any) => {
          if (!captchaRes?.valid) {
            this.isLoading = false;
            this.authForm.get('captcha')?.setValue('');
            this.refreshCaptcha();
            this.showError(captchaRes?.message || "Invalid CAPTCHA code. Please try again.");
            return;
          }
          this.proceedLogin();
        },
        error: () => {
          this.isLoading = false;
          this.authForm.get('captcha')?.setValue('');
          this.refreshCaptcha();
          this.showError("CAPTCHA verification failed. Please try again.");
        }
      });
    } catch (error) {
      this.isLoading = false;
      console.error("Login error:", error);
      this.showError("unexpectedError");
    }
  }

  private proceedLogin() {
    const { username, password } = this.authForm.value;
    const encryptedPassword = SecurityUtils.encryptSensitiveData(password);

    const type = this.loginValidationService.getType(username);
    let isValidUserType = false;
    const loginType = type;
    const upperUser = username.toUpperCase();

    if (this.loginMode === "department") {
      if (upperUser.startsWith("DP")) {
        this.loginType = "DEPARTMENT";
        isValidUserType = type === "username";
      } else {
        this.isLoading = false;
        this.authForm.get("username")?.setErrors({ invalidFormat: "invalidUsernameForLoginType" });
        this.authForm.get("username")?.markAsTouched();
        return;
      }
    } else if (this.loginMode === "ract") {
      if (
        upperUser.startsWith("RA") ||
        upperUser.startsWith("CT-TROUPE") ||
        upperUser.startsWith("MB")
      ) {
        this.loginType = "PUBLIC_PORTAL";
        isValidUserType = type === "username";
      } else {
        this.isLoading = false;
        this.authForm.get("username")?.setErrors({ invalidFormat: "invalidUsernameForLoginType" });
        this.authForm.get("username")?.markAsTouched();
        return;
      }
    } else {
      this.isLoading = false;
      this.authForm.get("username")?.setErrors({ invalidFormat: "invalidUsernameMode" });
      this.authForm.get("username")?.markAsTouched();
      return;
    }

    if (!isValidUserType) {
      this.isLoading = false;
      this.authForm.get("username")?.setErrors({ invalidFormat: "invalidUsernameFormatForLoginType" });
      this.authForm.get("username")?.markAsTouched();
      return;
    }

    this.authService.login(username, encryptedPassword, loginType).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.handleLoginResponse(response?.data?.login);
      },
      error: (error: any) => {
        this.isLoading = false;
        this.handleLoginError(error);
        this.refreshCaptcha();
      },
    });
  }

  private handleLoginResponse(user: any): void {
    if (!user) {
      this.showError("invalidCredentials");
      return;
    }

    this.alert = {
      alertType: "success",
      alertMessage: "loginSuccess",
    };
    this.authService.storeSession(user);
    this.navigateAfterLogin(user);
  }

  private handleLoginError(error: any): void {
    console.error("Login failed:", error);
    // Apollo sets networkError when the request itself failed (server
    // down/unreachable, timeout, 500/502/503, malformed response) — the
    // server never actually evaluated the credentials, so telling the
    // user their username/password is wrong is misleading. graphQLErrors
    // means the server responded normally and rejected the login, which
    // is a genuine invalid-credentials case.
    if (error?.networkError) {
      this.showError("serverError");
      return;
    }

    // The server distinguishes a lockout from bad credentials, so don't
    // collapse both into "invalid credentials" — a locked-out user told
    // their password is wrong will keep changing it and never learn that
    // waiting is what actually fixes it. Matched on message text because
    // these come back as plain UnauthorizedExceptions with no error code;
    // if the backend ever adds codes, switch to those instead.
    const serverMessage: string =
      error?.graphQLErrors?.[0]?.message || error?.message || "";

    if (/has not been selected|selection process/i.test(serverMessage)) {
      this.showError("ctNotSelectedYet");
      return;
    }

    if (/deactivated/i.test(serverMessage)) {
      this.showError("ctDeactivated");
      return;
    }

    if (/locked/i.test(serverMessage)) {
      if (/attempts remaining/i.test(serverMessage)) {
        const match = serverMessage.match(/(\d+)/);
        const count = match ? match[1] : "4";
        this.alert = { alertType: "error", alertMessage: `attempts_remaining:${count}` };

        const snackMsg = this.currentLang === 'or'
          ? `ଅବୈଧ ପ୍ରମାଣପତ୍ର । ଆପଣଙ୍କ ଆକାଉଣ୍ଟ୍ ଅବରୁଦ୍ଧ ହେବା ପୂର୍ବରୁ ଆପଣଙ୍କ ପାଖରେ ${count} ଟି ଚେଷ୍ଟା ବାକି ଅଛି ।`
          : serverMessage;

        this.snackBar.open(snackMsg, "Close", {
          duration: 7000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        });
        return;
      }
      // "Too many failed login attempts…" is thrown at the moment the lock
      // is applied; "Account temporarily locked…" on later attempts.
      this.showError(
        /too many/i.test(serverMessage) ? "tooManyAttempts" : "accountLocked",
      );
      return;
    }

    this.showError("invalidCredentials");
  }

  getAlertMessage(): string {
    const msgKey = this.alert.alertMessage;
    if (!msgKey) return '';

    if (msgKey.startsWith('attempts_remaining:')) {
      const attempts = msgKey.split(':')[1];
      if (this.currentLang === 'or') {
        return `ଅବୈଧ ପ୍ରମାଣପତ୍ର । ଆପଣଙ୍କ ଆକାଉଣ୍ଟ୍ ଅବରୁଦ୍ଧ ହେବା ପୂର୍ବରୁ ଆପଣଙ୍କ ପାଖରେ ${attempts} ଟି ଚେଷ୍ଟା ବାକି ଅଛି ।`;
      } else {
        return `Invalid credentials. You have ${attempts} attempts remaining before your account gets locked.`;
      }
    }

    return this.t[msgKey] || msgKey;
  }

  private navigateAfterLogin(user: any): void {
    if (user.type === "AGENCY") {
      // Show payment fee notice once after this login on the RA dashboard.
      sessionStorage.setItem("showRaPaymentNotice", "1");
      this.router.navigate(["/portal/request-agency"]).then(nav => {

      }).catch(err => {
        console.error("Navigation to RA failed:", err);
      });
      return;
    }
    if (user.type === "TROUPE") {
      this.router.navigate(["/portal/dashboard"]).then(nav => {

      }).catch(err => {
        console.error("Navigation to Troupe failed:", err);
      });
      return;
    }
    if (user.type === "ARTIST") {
      this.router.navigate(["/portal/member"]).then(nav => {

      }).catch(err => {
        console.error("Navigation to Artist failed:", err);
      });
      return;
    }
    if (user.roleName === 'ADMIN') user.roleName = "SUPPORT"
    const roleOrType = (user.roleName || user.type || "").toUpperCase();

    switch (roleOrType) {
      case "DYDIRECTOR":
      case "DCO":
      case "ASO":
      case "DIRECTOR":
      case "MINISTER":
        this.router.navigate(["/department/dashboard"]).then(nav => {
        }).catch(err => {
          console.error("Navigation to department failed:", err);
        });
        break;
      case "AGENCY":
        sessionStorage.setItem("showRaPaymentNotice", "1");
        this.router.navigate(["/portal/request-agency"]).then(nav => {
        }).catch(err => {
          console.error("Navigation to RA failed:", err);
        });
        break;
      case "TROUPE":
        this.router.navigate(["/portal/dashboard"]).then(nav => {
        }).catch(err => {
          console.error("Navigation to Troupe failed:", err);
        });
        break;
      case "ARTIST":
        this.router.navigate(["/portal/member"]).then(nav => {
        }).catch(err => {
          console.error("Navigation to Artist failed:", err);
        });
        break;
      case "JURY":
        this.router.navigate(["/department/dashboard"]).then(nav => {
        }).catch(err => {
          console.error("Navigation to department failed:", err);
        });
        break;
      case "SUPPORT":
      case "ADMIN":
        this.router.navigate(["/department/dashboard"]).then(nav => {
        }).catch(err => {
          console.error("Navigation to support failed:", err);
        });
        break;
      default:
        this.router.navigate(["/"]).then(nav => {
        }).catch(err => {
          console.error("Navigation to home failed:", err);
        });
    }
  }

  private showError(errorMsg: string): void {
    this.alert = { alertType: "error", alertMessage: errorMsg };
    const translatedMsg = this.t[errorMsg] || errorMsg;
    this.snackBar.open(translatedMsg, "Close", {
      duration: 5000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: ["error-snackbar"],
    });
  }
}
